import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Grid,
  Loader,
  NativeSelect,
  Select,
  Table
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import useAxios from 'axios-hooks'
import { useInstallationContext } from '../InstallationContext/InstallationContext'
import { useState } from 'react'
import dayjs from 'dayjs'
import { ResponsiveBar } from '@nivo/bar'

type UserFeedbackAnalyticsRow = {
  userId: string
  email: string
  name: string | undefined
  imageUrl: string | undefined
  feedbackGiven: number
  feedbackReceived: number
}

type FeedbackTypeKey =
  | 'amazing_job'
  | 'kind_candid'
  | 'lemon_twist'
  | 'null'
  | 'thank_you'
  | 'well_done'
  | 'legend'
  | 'impressive'

type FeedbackAnalytics = {
  feedback: {
    totalCount: number
    typeAndCount: Record<
      FeedbackTypeKey,
      {
        count: number
        title: string
      }
    >
  }
}

const xDate = () => dayjs().day(-2).hour(16).minute(30)

const downloadUserFeedbackAnalyticsCsv = (rows: UserFeedbackAnalyticsRow[]) => {
  const header = [
    'email',
    'name',
    'feedbackGiven',
    'feedbackReceived'
  ].join(',')
  const escapeCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`
  const body = rows
    .map((row) =>
      [
        escapeCsvValue(row.email),
        escapeCsvValue(row.name ?? ''),
        row.feedbackGiven,
        row.feedbackReceived
      ].join(',')
    )
    .join('\n')

  const blob = new Blob([`${header}\n${body}`], {
    type: 'text/csv;charset=utf-8;'
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `leaderboard-${dayjs().format('YYYY-MM-DD')}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export const Analytics = () => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    xDate().toDate(),
    dayjs().toDate()
  ])

  const [startDate, endDate] = dateRange

  const { id } = useInstallationContext()

  const [sortBy, setSortBy] = useState<'feedbackGiven' | 'feedbackReceived'>(
    'feedbackGiven'
  )

  const [{ data, loading }] = useAxios<{
    data: FeedbackAnalytics
    total: number
  }>({
    url: `installations/${id}/analytics`,
    params: {
      start: startDate ? dayjs(startDate).toISOString() : undefined,
      end: endDate ? dayjs(endDate).toISOString() : undefined
    }
  })

  const [{ data: data3, loading: loading3 }] = useAxios<{
    data: UserFeedbackAnalyticsRow[]
    total: number
  }>({
    url: `installations/${id}/user-analytics`,
    params: {
      startDateGTE: startDate ? dayjs(startDate).toISOString() : undefined,
      endDateLTE: endDate ? dayjs(endDate).toISOString() : undefined
    }
  })

  if (loading3) {
    return <Loader />
  }

  return (
    <Card style={{ padding: 30 }}>
      <h4>Analytics</h4>
      <DatePickerInput
        type="range"
        disabled={loading}
        label="Select date range"
        placeholder="Select date range"
        description="Optional. Leave blank to include all data."
        value={dateRange}
        onChange={setDateRange}
        clearable
        maw={400}
      />
      <Divider my="xs" size="sm" />
      <Grid>
        {loading ? (
          <Col
            span={12}
            style={{
              height: 300,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Loader />
          </Col>
        ) : data?.data.feedback.totalCount === 0 ? (
          <Col
            span={12}
            style={{
              height: 300,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <h5
              style={{
                color: 'gray'
              }}
            >
              No kudos this week
            </h5>
          </Col>
        ) : (
          <>
            <Col
              span={4}
              style={{
                height: 300,
                flexDirection: 'column',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <h1>{data?.data.feedback.totalCount}</h1>
              <div>total feedback</div>
            </Col>
            <Col span={8} style={{ height: 300 }}>
              <ResponsiveBar
                data={Object.entries(
                  data?.data.feedback.typeAndCount ?? {}
                ).map(([key, value]) => ({
                  id: value.title,
                  count: value.count,
                  title: value.title
                }))}
                // enableLabel={false}
                label={(e) => `${e.data.count}`}
                // keys={Object.keys(data?.data.feedback.typeAndCount ?? {})}
                keys={['count']}
                margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={{ scheme: 'paired' }}
                borderColor={{
                  from: 'color',
                  modifiers: [['darker', 1.6]]
                }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legendPosition: 'middle',
                  legendOffset: 32
                }}
                legendLabel={(e) => e?.data?.title || ''}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Count',
                  legendPosition: 'middle',
                  legendOffset: -40,
                  format: (e) => (Math.floor(e) === e && e) || ''
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{
                  from: 'color',
                  modifiers: [['darker', 1.6]]
                }}
                role="application"
              />
              {/* <ResponsiveBar
            data={
              Object.entries(data?.data.feedback.typeAndCount ?? {}).map(([key, value]) => ({
                id: key,
                count: value
              }))
            }
          /> */}
            </Col>
          </>
        )}

        <Col span={12}>
          <Divider my="xs" size="sm" />
          <h5>Leaderboard</h5>
        </Col>
        <Col
          span={12}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}
        >
          <Select
            defaultValue={'feedbackGiven'}
            onChange={(e) => {
              if (!e) return
              setSortBy(e as 'feedbackGiven' | 'feedbackReceived')
            }}
            label="Sort"
            data={[
              { value: 'feedbackGiven', label: 'Feedback given' },
              { value: 'feedbackReceived', label: 'Feedback received' }
            ]}
          />
          <Button
            disabled={!data3?.data?.length}
            onClick={() => downloadUserFeedbackAnalyticsCsv(data3?.data ?? [])}
          >
            Download CSV
          </Button>
        </Col>
        <Col span={12}>
          <Table withBorder style={{ backgroundColor: 'white' }}>
            <thead>
              <tr>
                <th>Teammate</th>
                <th>Name</th>
                <th>Email</th>
                <th>Kudos given</th>
                <th>Kudos received</th>
              </tr>
            </thead>
            <tbody>
              {data3?.data
                .slice()
                .sort((a, b) => (a[sortBy] > b[sortBy] ? -1 : 1))
                .map((a) => (
                  <tr key={a.userId}>
                    <td style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={a.imageUrl} />
                    </td>
                    <td>{a.name}</td>
                    <td>{a.email}</td>
                    <td>{a.feedbackGiven}</td>
                    <td>{a.feedbackReceived}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Col>
      </Grid>
    </Card>
  )
}
